<script setup lang="ts">
import { computed } from 'vue';
import { supportedLocales } from '@pkg/config/languages';
import type { LOCALE_CODE } from '@pkg/config/languages/localeCodes';
import { getIso2Digit } from '@pkg/utilities/i18n';
import ModularBlocksRenderer from '@pkg/modular-blocks/frontend/ModularBlocksRenderer.vue';
import { useSanityPageSeoMeta } from '@pkg/sanity-utils/composables/useSanityPageSeoMeta';
import { usePageVisibilityRobots } from '@pkg/sanity-utils/composables/usePageVisibilityRobots';
import Page404 from '~/components/Page404.vue';
import { useHrefLinks } from '~/composables/useHrefLinks';

/**
 * Catch-all page (mirrors apps/web/pages/[...slug].vue).
 * Locale home → business homepage singleton.
 * Other paths → business-audience CMS page by slug, else soft 404.
 *
 * Canonical, hreflang alternates, og:url, and og:locale come from i18n
 * (`NUXT_PUBLIC_DOMAIN`). Alternates are limited to locales that have this
 * page. og:url is the canonical of this page even when no other locale exists.
 * A page meta title or description replaces the site default. An empty page
 * field leaves the site default in place. The document title is used only
 * when neither the page nor the site has a meta title.
 */
const { data } = await useSanityPageQuery();
const { data: manifest } = await useSiteManifestQuery();
const route = useRoute();

const head = useLocaleHead({
  addDirAttribute: true,
  identifierAttribute: 'id',
  addSeoAttributes: true,
});

useSanityPageSeoMeta(() => data.value?.page?.seoSocial);
usePageVisibilityRobots(() => data.value?.page?.pageVisibility);

const documentTitle = () => {
  const pageMetaTitle = data.value?.page?.seoSocial?.metaTitle?.trim();
  const siteMetaTitle = manifest.value?.defaultSeo?.metaTitle?.trim() ||
    manifest.value?.defaultSeo?.metaTitleFallback?.trim();
  if (pageMetaTitle || siteMetaTitle) {
    return undefined;
  }
  return data.value?.page?.title?.trim() || undefined;
};

useSeoMeta({
  title: documentTitle,
  // The site layer sets og:title to the templated title. Only fill og:title
  // when that layer is absent, so an empty page meta title does not replace it.
  ogTitle: () => (manifest.value?.defaultSeo ? undefined : documentTitle()),
  ogUrl: () => {
    if (!data.value?.page) {
      return undefined;
    }
    return head.value.link?.find(link => link.rel === 'canonical')?.href;
  },
});

const { getLocalizedHref } = useHrefLinks(route);

type HeadLink = {
  id?: string;
  rel?: string;
  href?: string;
  hreflang?: string;
};

type HeadMeta = {
  id?: string;
  property?: string;
  content?: string;
};

const isCanonicalLink = (link: HeadLink) => link.rel === 'canonical';

const filteredHeadLinks = computed(() => (head.value.link || []).filter((link: HeadLink) =>
  isCanonicalLink(link) ||
  data.value?.page?._translations?.some(
    translation => translation?.language === link.hreflang,
  ),
));

// No canonical on 404s: it would point at the broken URL itself.
const renderLinkCondition = (link: HeadLink) =>
  isCanonicalLink(link)
    ? !!data.value?.page
    : supportedLocales.includes(link.hreflang as LOCALE_CODE);

const getHref = (link: HeadLink) => {
  const translations = data.value?.page?._translations;
  if (translations?.length) {
    const localizedHref = getLocalizedHref(link, translations, data.value?.page?._type);
    if (localizedHref) {
      return localizedHref;
    }
  }
  return link.href;
};

const getHreflang = (link: HeadLink) => {
  if (isCanonicalLink(link) || !link.hreflang) {
    return undefined;
  }

  const isHyphenated = link.hreflang.includes('-');
  const countryString = getIso2Digit(link.hreflang as LOCALE_CODE).toUpperCase() || '';
  const languageString = isHyphenated ? link.hreflang.substring(0, 2) : link.hreflang;
  return `${languageString}-${countryString}`;
};

useHead(computed(() => {
  const page = data.value?.page;
  const link = filteredHeadLinks.value
    .filter(item => renderLinkCondition(item))
    .map(item => ({
      key: item.id,
      rel: item.rel,
      href: getHref(item),
      hreflang: getHreflang(item),
    }));

  const meta = ((head.value.meta || []) as HeadMeta[])
    .filter((item) => {
      if (item.property === 'og:url') {
        return false;
      }
      if (item.property === 'og:locale:alternate') {
        const localeFromContent = item.content?.replace('_', '-');
        return page?._translations?.some(
          translation => translation?.language === localeFromContent,
        );
      }
      return true;
    })
    .map(item => ({
      key: item.id,
      property: item.property,
      content: item.content,
    }));

  return {
    htmlAttrs: {
      lang: head.value.htmlAttrs?.lang,
      dir: head.value.htmlAttrs?.dir,
    },
    link,
    meta,
  };
}));
</script>

<template>
  <ModularBlocksRenderer
    v-if="data?.page"
    :blocks="data.page.content"
    type="outer"
  />
  <Page404 v-else />
</template>
